import React, { useCallback, useMemo } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Alert, Box, Chip, Collapse, Stack, TableCell, TableRow, Typography } from '@mui/material';

import { getDropdownCellSx, handleExpandRowKeyDown } from '../surcharges/surchargeTabLayout';

import EgsGuestTypeTable from './EgsGuestTypeTable';
import EgsMatchStatusChip from './EgsMatchStatusChip';
import EgsRoomRateList from './EgsRoomRateList';
import {
  type EgsRoomMatch,
  getMatchRoomRateCount,
  getRoomDataState,
  getRoomRateIds,
  unhandledEgsMatch,
} from './egsRoomMatcher';
import { formatSlotList } from './egsUtils';
import type { EgsRoomGroup } from './types';

interface Props {
  columnCount: number;
  group: EgsRoomGroup;
  hasRoomTypes: boolean;
  isExpanded: boolean;
  isLoadingRoomTypes: boolean;
  match: EgsRoomMatch;
  roomTypes: Array<App.RoomType>;
  roomTypesError: unknown;
  onToggleExpanded: (roomLabel: string) => void;
}

function renderRoomMatchDetail(match: EgsRoomMatch, roomTypes: Array<App.RoomType>) {
  if (match.source === 'all_rooms') {
    if (!roomTypes.length) {
      return <Alert severity="info">Salesforce named this for all rooms, but this offer sells no room types.</Alert>;
    }

    return (
      <Stack spacing={1}>
        <Alert severity="info">
          Salesforce named this for all rooms, so it covers all {roomTypes.length} room type
          {roomTypes.length === 1 ? '' : 's'} the offer sells.
        </Alert>
        <EgsRoomRateList
          emptyMessage="These room types sell no room rates on this offer."
          roomRateIds={getRoomRateIds(roomTypes)}
          roomTypes={roomTypes}
        />
      </Stack>
    );
  }

  if (!roomTypes.length) {
    return <Alert severity="info">This offer has no room rates, so there is nothing to match this against.</Alert>;
  }

  // Candidates, never a match: which plan it prices is decided on apply.
  if (match.source === 'rate_plan_qualified') {
    const candidateRoomTypes = match.matchedRoomTypes.length ? match.matchedRoomTypes : roomTypes;

    return (
      <Stack spacing={1}>
        <Alert severity="warning">
          Salesforce priced this for <strong>{match.qualifierText}</strong> only, which is a rate plan rather than a
          room. You pick the matching rate plan when you apply.
        </Alert>
        <EgsRoomRateList
          emptyMessage="These room types sell no room rates on this offer."
          roomRateIds={getRoomRateIds(candidateRoomTypes)}
          roomTypes={candidateRoomTypes}
        />
      </Stack>
    );
  }

  const noMatch = (
    <Alert severity="info">
      No room on this offer resembles this name, so this surcharge cannot be applied to anything. Check the name in
      Salesforce.
    </Alert>
  );

  switch (match.source) {
    case 'suggested':
      return (
        <Alert severity="warning">
          This name is close to a room on this offer but not close enough to match it for you. You pick the rooms it
          applies to when you apply it from Salesforce.
        </Alert>
      );

    case 'unmatched':
      return noMatch;

    case 'matched':
      return (
        <EgsRoomRateList
          emptyMessage="The matched room type sells no room rates on this offer."
          roomRateIds={match.roomRateIds}
          roomTypes={roomTypes}
        />
      );

    default:
      return unhandledEgsMatch(match, noMatch);
  }
}

export default function EgsRoomRow({
  columnCount,
  group,
  hasRoomTypes,
  isExpanded,
  isLoadingRoomTypes,
  match,
  roomTypes,
  roomTypesError,
  onToggleExpanded,
}: Props) {
  const toggleExpanded = useCallback(() => onToggleExpanded(group.roomLabel), [group.roomLabel, onToggleExpanded]);
  // With no room data a count would be a fabricated zero, so the chip's own state gates it.
  const roomRateCount = useMemo(() => {
    const roomDataState = getRoomDataState({
      error: roomTypesError,
      hasRoomTypes,
      isLoading: isLoadingRoomTypes,
    });

    return roomDataState === 'ready' ? getMatchRoomRateCount(match, roomTypes) : null;
  }, [hasRoomTypes, isLoadingRoomTypes, match, roomTypes, roomTypesError]);

  return (
    <>
      <TableRow
        hover
        role="button"
        sx={{ cursor: 'pointer' }}
        tabIndex={0}
        onClick={toggleExpanded}
        onKeyDown={(event) => handleExpandRowKeyDown(event, toggleExpanded)}
      >
        <TableCell>{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</TableCell>
        <TableCell>
          <Typography fontWeight={600} variant="body2">
            {group.roomLabel || 'Unnamed'}
          </Typography>
          {group.conflictingSlots.length > 0 && (
            <Typography color="warning.main" component="p" variant="caption">
              Salesforce has more than one price for {formatSlotList(group.conflictingSlots)} in this room
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {group.slots.map((slot) => (
              <Chip key={slot} label={slot} size="small" variant="outlined" />
            ))}
          </Stack>
        </TableCell>
        <TableCell align="center">
          <Chip label={group.rows.length} size="small" variant="outlined" />
        </TableCell>
        <TableCell align="center">
          <EgsMatchStatusChip
            error={roomTypesError}
            hasRoomTypes={hasRoomTypes}
            isLoading={isLoadingRoomTypes}
            match={match}
          />
        </TableCell>
        <TableCell align="center">
          {roomRateCount === null ? '—' : <Chip label={roomRateCount} size="small" variant="outlined" />}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={columnCount} sx={getDropdownCellSx(isExpanded)}>
          <Collapse unmountOnExit in={isExpanded} sx={{ width: '100%' }} timeout="auto">
            <Box sx={{ bgcolor: 'grey.50', px: 2, py: 1 }}>
              <Box sx={{ py: 1 }}>
                {roomTypesError ? (
                  <Alert severity="error">Unable to load room types, so the matching room rates are unknown.</Alert>
                ) : (
                  renderRoomMatchDetail(match, roomTypes)
                )}
              </Box>
              <EgsGuestTypeTable rows={group.rows} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
